import React, { useContext, useState } from "react";
import axios from "axios";
import { base_url, stripe_key } from "../../config";
import RegistrationContext from "../../contexts/RegistrationContext";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  CreditCard,
  GraduationCap,
  Loader2,
  UploadIcon,
} from "lucide-react";
import StudentProof from "../RegistrationType/StudentProof";
import { loadStripe } from "@stripe/stripe-js";

if (!stripe_key) {
  throw new Error("Stripe key is not defined");
}
const stripePromise = loadStripe(stripe_key);

interface StudentStatusProps {
  setActiveOption: (option: string) => void;
}

export const StudentStatus: React.FC<StudentStatusProps> = ({
  setActiveOption,
}) => {
  const { data, setData, setError } = useContext(RegistrationContext);
  const [, setIsProcessingPayment] = useState(false);

  const handleProceedToPayment = async () => {
    setIsProcessingPayment(true);

    try {
      const response = await axios.post(
        `${base_url}/api/create-checkout-session-upgrade`,
        {}
      );

      const stripe = await stripePromise;
      if (stripe) {
        await stripe.redirectToCheckout({ sessionId: response.data.id });
      } else {
        setError("Stripe initialization failed. Please try again.");
      }
    } catch (error) {
      setError("Failed to initiate payment. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleProceedToUpgrade = async () => {
    try {
      await axios.post(`${base_url}/api/student-upgrade`, {});

      setData({ ...data, registration_type: "regular" });
      setActiveOption("Registration Details");
    } catch (error) {
      setError("Failed to initiate payment. Please try again.");
    }
  };

  function StudentProcessing() {
    const [isExpanded, setIsExpanded] = useState(true);

    return (
      <div className="bg-white rounded-lg shadow border-l-4 border-blue-500 mb-3">
        <div
          className="flex items-center justify-between p-3 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center">
            <Clock className="h-4 w-4 text-blue-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-800">
              Student status validation
            </h3>
          </div>
          <div className="flex items-center">
            <Loader2 className="h-3 w-3 text-blue-500 animate-spin mr-1" />
            <span className="text-xs font-medium text-blue-500 mr-2">
              Processing
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="p-3 border-t border-gray-100">
            <p className="text-xs text-gray-600 mb-2">
              Your student validation is currently being processed. This
              typically takes 2-3 business days.
            </p>

            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
              <div className="bg-blue-500 h-1.5 rounded-full w-1/2"></div>
            </div>

            {/* <p className="text-xs text-gray-500">Estimated completion: 48 hours</p> */}
          </div>
        )}
      </div>
    );
  }

  function StudentOK() {
    return (
      <>
        <div className="bg-white rounded-lg shadow border-l-4 border-green-500 mb-3">
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center">
              <GraduationCap className="h-4 w-4 text-green-500 mr-2" />
              <h3 className="text-sm font-medium text-gray-800">
                Your student status has been validated
              </h3>
            </div>
          </div>
        </div>
        {data.registration_type === "student" && (
          <div className="bg-white rounded-lg shadow border-l-4 border-blue-500 mb-3">
            <div className="p-3 border-t border-gray-100">
              <div className="flex items-center">
                <CreditCard className="h-4 w-4 text-blue-500 mr-2" />
                <h3 className="text-sm font-medium text-gray-800">
                  Upgrade to student with paper
                </h3>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                You can upgrade your registration to "student with paper" and
                register papers of your authorship (Cost $
                {data.student_upgrade_cost}). Your student status remains
                validated.
              </p>
              <div className="flex justify-end">
                <button
                  className="mt-4 rounded-md px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  onClick={() => handleProceedToPayment()}
                >
                  Upgrade{" "}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  function StudentKO({ r = "" }: { r: string }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [selectedOption, setSelectedOption] = useState<
      "upload" | "email" | "upgrade"
    >("upload");
    const [fileReady, setFileReady] = useState("");

    const submitNewFileHanlder = async () => {
      try {
        await axios.post(`${base_url}/api/student_proof_submitted`, {});
        setData({ ...data, student_validation: "pending" });
      } catch (error: any) {
        setError(
          "Unexpected error. " + error.response?.data?.message || error.message
        );
      }
    };

    const disabledButton =
      selectedOption === "upload" && (!fileReady || fileReady === "");
    return (
      <div className="bg-white rounded-lg shadow border-l-4 border-red-500 mb-3">
        <div className="flex-1 ">
          <div className="mx-auto max-w-3xl">
            <div>
              <div>
                <div
                  className="flex items-center justify-between pt-3 mb-4 cursor-pointer"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                    <h3 className="text-sm font-medium text-gray-800">
                      Student validation failed{" "}
                    </h3>
                  </div>
                </div>

                <div className="mb-4 ">
                  <div className=" rounded-md mb-2">
                    <p className="text-xs text-gray-500">
                      Student verification was unsuccessful.
                    </p>
                  </div>
                </div>

                <h3 className="mb-4 font-medium">Reason:</h3>
                <p className="rounded-md bg-gray-50 p-3 mb-4 text-gray-700">
                  {r}
                </p>
                <h3 className="mb-4 font-medium">Available Options:</h3>
                <div className="space-y-4">
                  <div
                    className={`flex items-start space-x-3 rounded-lg  p-4 cursor-pointer ${
                      selectedOption === "upload" ? "" : ""
                    }`}
                    onClick={() => setSelectedOption("upload")}
                  >
                    <div className="flex h-5 w-5 items-center justify-center">
                      <input
                        type="radio"
                        id="upload"
                        name="option"
                        value="upload"
                        checked={selectedOption === "upload"}
                        onChange={() => setSelectedOption("upload")}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="upload"
                        className="flex cursor-pointer items-center text-base font-medium"
                      >
                        <UploadIcon className="mr-2 h-5 w-5 text-blue-500" />
                        Upload a new proof of student status
                      </label>
                      <p className="mt-1 text-sm text-gray-500">
                        Submit a clear, current document that verifies your
                        enrollment as a student.
                      </p>
                    </div>
                  </div>
                  {selectedOption === "upload" && (
                    <div className="ml-12">
                      <StudentProof ready={setFileReady} />
                    </div>
                  )}
                  <div
                    className={`flex items-start space-x-3 rounded-lg  p-4 cursor-pointer ${
                      selectedOption === "upgrade" ? "" : ""
                    }`}
                    onClick={() => setSelectedOption("upgrade")}
                  >
                    <div className="flex h-5 w-5 items-center justify-center">
                      <input
                        type="radio"
                        id="upgrade"
                        name="option"
                        value="upgrade"
                        checked={selectedOption === "upgrade"}
                        onChange={() => setSelectedOption("upgrade")}
                        className="h-4 w-4 text-green-600 cursor-pointer"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="upgrade"
                        className="flex cursor-pointer items-center text-base font-medium"
                      >
                        <CreditCard className="mr-2 h-5 w-5 text-green-500" />
                        Upgrade to regular delegate registration
                      </label>
                      <p className="mt-1 text-sm text-gray-500">
                        {data.registration_type === "student-paper"
                          ? "Upgrade to regular delegate registration for free (no student reception included)."
                          : `Upgrade to regular delegate registration for $${data.student_upgrade_cost}.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 border-t  px-6 py-4">
                <button className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  disabled={disabledButton}
                  className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                    disabledButton
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                  onClick={
                    selectedOption === "upload"
                      ? () => submitNewFileHanlder()
                      : data.registration_type === "student-paper"
                      ? () => handleProceedToUpgrade()
                      : () => handleProceedToPayment()
                  }
                >
                  {selectedOption === "upload"
                    ? "Submit New Student Proof"
                    : data.registration_type === "student-paper"
                    ? "Upgrade to regular delegate Registration"
                    : "Proceed to Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  switch (data.student_validation) {
    case "pending":
      return <StudentProcessing />;
    case "ok":
      return <StudentOK />;
    case "ko":
      return <StudentKO r={data.student_ko_reason || ""} />;
    default:
      return null; // Always provide a default to prevent undefined returns
  }
};
